import { forwardRef, type ReactElement } from 'react';

import { Button } from '@dynatrace/strato-components/buttons';
import { Flex } from '@dynatrace/strato-components/layouts';

import { Modal, type ModalProps } from '../../../../overlays/modal/Modal.js';

/**
 * @internal
 */
export interface DataTableV2SettingsModalProps
  extends Omit<ModalProps, 'footer' | 'size'> {
  /** The callback that is called for resetting changes. */
  onReset?: () => void;
  /** The callback that is called for applying changes  */
  onApply?: () => void;
  /** The title of the modal. */
  title: string | ReactElement;
  /** Labels for the reset, cancel and apply buttons. */
  controlLabels: {
    reset?: string;
    cancel?: string;
    apply?: string;
  };
  /** Determines whether the reset and/or the apply buttons are disabled. */
  disableButtons?: { reset?: boolean; apply?: boolean };
}

/**
 * Modal that allows to apply, reset or cancel changes on the given settings.
 * @internal
 */
export const DataTableV2SettingsModal = forwardRef<
  HTMLDivElement,
  DataTableV2SettingsModalProps
>((props, forwardedRef) => {
  const {
    title,
    dismissible = true,
    show,
    children,
    onDismiss,
    onReset,
    onApply,
    controlLabels,
    disableButtons,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    className: consumerClassName,
    style: consumerStyle,
  } = props;

  const showFooter = onDismiss || onReset || onApply;

  return (
    <Modal
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      ref={forwardedRef}
      className={consumerClassName}
      style={consumerStyle}
      title={title}
      size={'small'}
      show={show}
      dismissible={dismissible}
      onDismiss={onDismiss}
      footer={
        showFooter && (
          <Flex justifyContent="space-between" width="100%">
            {onReset && (
              <Button
                onClick={onReset}
                variant="emphasized"
                disabled={disableButtons?.reset === true}
              >
                {controlLabels?.reset}
              </Button>
            )}

            <Flex justifyContent="flex-end" gap={8}>
              {onDismiss && (
                <Button onClick={onDismiss} variant="default">
                  {controlLabels?.cancel}
                </Button>
              )}
              {onApply && (
                <Button
                  onClick={onApply}
                  variant="accent"
                  color="primary"
                  disabled={disableButtons?.apply === true}
                >
                  {controlLabels?.apply}
                </Button>
              )}
            </Flex>
          </Flex>
        )
      }
    >
      {children}
    </Modal>
  );
});
